import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  App,
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Skeleton,
  Slider,
  Space,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  EventContentMode,
  PlaceSuggestion,
  useCreateEventMutation,
  useGetCountriesQuery,
  useGetEventQuery,
  useGetPlatformSettingsQuery,
  useLazyPlaceDetailsQuery,
  useLazyPlacesAutocompleteQuery,
  useUpdateEventMutation,
} from "api";
import { useAppSelector } from "store/hooks";
import { getRouteByName } from "libs";
import { getErrorMessage } from "components";
import { EventModerationCard } from "./EventModerationCard";
import { EventContentModeCard } from "./EventContentModeCard";

interface FormValues {
  title: string;
  description?: string;
  locationLabel: string;
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
  startsAt: Dayjs;
  endsAt: Dayjs;
  // Create only. On an existing event the mode is edited through
  // EventContentModeCard, because it stays writable once the event is
  // live while every other field here does not.
  contentMode: EventContentMode;
}

interface RadiusInputProps {
  value?: number;
  onChange?: (value: number) => void;
  // Active ceiling in metres (admin-configured via platform settings).
  maxMeters: number;
}

const RADIUS_MIN = 50;
// Fallback ceiling used until the platform setting loads (or if the admin
// lacks read permission). The live ceiling comes from
// platform_settings.eventMaxRadiusKm and is passed in as `maxMeters`.
const DEFAULT_RADIUS_MAX = 3_000_000;
// Slider works on a high-resolution LOG-mapped axis (0..SLIDER_RES)
// instead of raw meters. A linear meter-axis would cluster every
// "common" value (50m–10km) into the first 2% of the slider — marks
// stack on top of each other and the user can't dial a small radius
// precisely. Log mapping spreads the marks evenly across the bar.
const SLIDER_RES = 1000;
const LOG_MIN = Math.log10(RADIUS_MIN);

const metersToSlider = (m: number, maxMeters: number): number => {
  const logMax = Math.log10(maxMeters);
  const clamped = Math.max(RADIUS_MIN, Math.min(maxMeters, m));
  const log = Math.log10(clamped);
  return Math.round(((log - LOG_MIN) / (logMax - LOG_MIN)) * SLIDER_RES);
};

// Snap raw meters to a sensible step that scales with magnitude. Below
// 1km we step in 50m (matches GPS noise floor); 100m up to 5km; 500m
// up to 50km; 1km to 100km; 5km above. Keeps the displayed value tidy
// regardless of where on the log axis the user landed.
const snapMeters = (m: number, maxMeters: number): number => {
  let step: number;
  if (m < 1_000) step = 50;
  else if (m < 5_000) step = 100;
  else if (m < 50_000) step = 500;
  else if (m < 100_000) step = 1_000;
  else step = 5_000;
  const snapped = Math.round(m / step) * step;
  return Math.max(RADIUS_MIN, Math.min(maxMeters, snapped));
};

const sliderToMeters = (pos: number, maxMeters: number): number => {
  const logMax = Math.log10(maxMeters);
  const log = LOG_MIN + (pos / SLIDER_RES) * (logMax - LOG_MIN);
  return snapMeters(Math.pow(10, log), maxMeters);
};

const formatMeters = (m: number): string =>
  m >= 1_000 ? `${(m / 1_000).toFixed(m % 1_000 === 0 ? 0 : 1)} km` : `${m} m`;

// Marks live on the log axis so they spread evenly across the slider. The
// low/mid marks are fixed; the top mark tracks the active ceiling so the
// slider always ends at the admin-configured maximum.
const buildRadiusMarks = (maxMeters: number): Record<number, string> => ({
  [metersToSlider(50, maxMeters)]: "50m",
  [metersToSlider(500, maxMeters)]: "500m",
  [metersToSlider(2_000, maxMeters)]: "2km",
  [metersToSlider(10_000, maxMeters)]: "10km",
  [metersToSlider(100_000, maxMeters)]: "100km",
  [metersToSlider(maxMeters, maxMeters)]: formatMeters(maxMeters),
});

/**
 * Composite radius input — log-scaled Slider for visual selection
 * across 50m–500km, InputNumber for precise dial-in, and a current-
 * value chip so the admin always knows the active value at a glance.
 *
 * The form value is in METERS (unchanged contract); the slider's
 * internal position is log-mapped so the common range (50m–10km) takes
 * the left ~57% of the bar instead of squishing into 2%.
 */
const RadiusInput: React.FC<RadiusInputProps> = ({
  value,
  onChange,
  maxMeters,
}) => {
  const meters = typeof value === "number" ? value : RADIUS_MIN;
  const sliderPos = metersToSlider(meters, maxMeters);
  const marks = useMemo(() => buildRadiusMarks(maxMeters), [maxMeters]);

  return (
    <Flex vertical gap={20}>
      <Flex align="center" justify="space-between">
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          Raza activă
        </Typography.Text>
        <Typography.Text strong style={{ fontSize: 16 }}>
          {formatMeters(meters)}
        </Typography.Text>
      </Flex>

      <Slider
        value={sliderPos}
        onChange={(v) =>
          onChange?.(sliderToMeters(typeof v === "number" ? v : 0, maxMeters))
        }
        min={0}
        max={SLIDER_RES}
        step={1}
        marks={marks}
        tooltip={{
          formatter: (v) => formatMeters(sliderToMeters(v ?? 0, maxMeters)),
        }}
      />

      <InputNumber
        value={meters}
        onChange={(v) =>
          onChange?.(typeof v === "number" ? snapMeters(v, maxMeters) : RADIUS_MIN)
        }
        min={RADIUS_MIN}
        max={maxMeters}
        step={50}
        addonAfter="m"
        style={{ width: "100%" }}
      />
    </Flex>
  );
};

// Sentinel passed to the Places autocomplete to lift the country
// restriction entirely. The backend treats "all" as "no components
// filter" — same shape any unrecognised value would take.
const ALL_COUNTRIES_VALUE = "all";

/**
 * Shared create + edit. Mounted at /events/new and /events/:id.
 * The `id` param decides which mutation we fire on submit and
 * whether we pre-fill from a fetch.
 *
 * The location field is a 2-step combobox: typing fires Place
 * autocomplete via our backend; picking a suggestion resolves
 * place details (lat/lng + clean label) using the same session
 * token so Google bills the typing session at flat rate.
 */
export const EventEditPage = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const params = useParams<{ id?: string }>();
  const isEdit = Boolean(params.id);
  const eventId = params.id ? Number(params.id) : undefined;

  const [form] = Form.useForm<FormValues>();
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerList, setBannerList] = useState<UploadFile[]>([]);
  // Address text typed in the combobox; debounced into the variable
  // below which is what actually fires the Google Places call.
  // Prevents one request per keystroke (Google bills per session but
  // the network round-trip is still wasted noise).
  const [addressInput, setAddressInput] = useState<string>("");
  const [debouncedAddress, setDebouncedAddress] = useState<string>("");
  // ISO alpha-2 country code that biases the Google Places autocomplete
  // toward a specific country. "all" lifts the restriction globally —
  // used when creating events outside Romania (UK and others). RO stays
  // the default so the existing flow doesn't shift for admins who only
  // ever target Romania.
  const [searchCountry, setSearchCountry] = useState<string>("ro");

  // 181-country list reused across SPOR forms — same shape the user
  // location picker consumes. Cached at the api layer (long
  // keepUnusedDataFor in commonApi), so opening EventEditPage doesn't
  // re-hit the backend if the list was fetched anywhere earlier in
  // the session.
  const { data: countries = [], isLoading: isLoadingCountries } =
    useGetCountriesQuery({});

  // Admin-tunable maximum event radius (platform_settings.eventMaxRadiusKm,
  // in km). Drives both the slider ceiling and the validation rule below.
  // Falls back to 3000 km until it loads (or if this admin can't read
  // settings) — the backend re-validates against the live value anyway.
  const { data: platformSettings } = useGetPlatformSettingsQuery();
  const maxRadiusMeters =
    (platformSettings?.eventMaxRadiusKm ?? DEFAULT_RADIUS_MAX / 1_000) * 1_000;

  const countryOptions = useMemo(() => {
    const sorted = [...countries].sort((a, b) =>
      a.name.localeCompare(b.name, "ro"),
    );
    return [
      { value: ALL_COUNTRIES_VALUE, label: "Toate țările" },
      ...sorted
        .filter((c) => typeof c.code === "string" && c.code.length === 2)
        .map((c) => ({
          value: c.code.toLowerCase(),
          label: c.name,
        })),
    ];
  }, [countries]);

  // One session token per typing/picking flow keeps Google billing
  // flat. New UUID on each mount = one session per page visit.
  const sessionToken = useMemo(
    () =>
      `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
    [],
  );

  const { isAll, isEventsEdit } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions,
  );
  const canEdit = isAll || isEventsEdit;

  const { data, isLoading } = useGetEventQuery(eventId!, {
    skip: !isEdit || eventId === undefined,
  });

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const [
    triggerAutocomplete,
    { data: suggestions = [], isFetching: isAutocompleting },
  ] = useLazyPlacesAutocompleteQuery();
  const [triggerPlaceDetails] = useLazyPlaceDetailsQuery();

  useEffect(() => {
    if (!canEdit) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [canEdit, navigate]);

  useEffect(() => {
    if (isEdit && data) {
      form.setFieldsValue({
        title: data.title,
        description: data.description ?? undefined,
        locationLabel: data.locationLabel,
        centerLat: data.centerLat,
        centerLng: data.centerLng,
        radiusMeters: data.radiusMeters,
        startsAt: dayjs(data.startsAt),
        endsAt: dayjs(data.endsAt),
      });
      if (data.bannerUrl) {
        setBannerList([
          {
            uid: "current",
            name: "banner",
            status: "done",
            url: data.bannerUrl,
          },
        ]);
      }
    } else if (!isEdit) {
      // Sensible defaults for the create form: tomorrow 18:00–22:00
      // and a 500m radius (typical bar / club / small venue).
      form.setFieldsValue({
        radiusMeters: 500,
        startsAt: dayjs().add(1, "day").hour(18).minute(0).second(0),
        endsAt: dayjs().add(1, "day").hour(22).minute(0).second(0),
        contentMode: "mixed",
      });
    }
  }, [data, isEdit, form]);

  // Debounce: collapse rapid keystrokes into a single fetch after the
  // user stops typing for 400ms. Tuned for the typical "type a city
  // name" flow — long enough to avoid mid-word fetches, short enough
  // that the suggestion list feels responsive.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedAddress(addressInput.trim());
    }, 400);
    return () => window.clearTimeout(handle);
  }, [addressInput]);

  useEffect(() => {
    if (debouncedAddress.length < 2) return;
    triggerAutocomplete({
      q: debouncedAddress,
      sessionToken,
      country: searchCountry,
    });
  }, [debouncedAddress, sessionToken, searchCountry, triggerAutocomplete]);

  const handleAutocompleteSearch = (value: string) => {
    // Only update the local typing buffer here. The debounced effect
    // above is what hits Google.
    setAddressInput(value);
  };

  const handleAutocompleteSelect = async (
    _value: string,
    option: { suggestion?: PlaceSuggestion } & Record<string, unknown>,
  ) => {
    const suggestion = option.suggestion;
    if (!suggestion) return;

    try {
      const details = await triggerPlaceDetails({
        placeId: suggestion.placeId,
        sessionToken,
      }).unwrap();
      form.setFieldsValue({
        locationLabel: details.label || suggestion.mainText,
        centerLat: details.lat,
        centerLng: details.lng,
      });
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  const handleSubmit = async (values: FormValues) => {
    const payload = {
      title: values.title,
      description: values.description,
      locationLabel: values.locationLabel,
      centerLat: values.centerLat,
      centerLng: values.centerLng,
      radiusMeters: values.radiusMeters,
      startsAt: values.startsAt.toISOString(),
      endsAt: values.endsAt.toISOString(),
      ...(bannerFile ? { banner: bannerFile } : {}),
    };

    try {
      if (isEdit && eventId) {
        await updateEvent({ id: eventId, ...payload }).unwrap();
        notification.success({ message: "Evenimentul a fost actualizat." });
      } else {
        await createEvent({
          ...payload,
          contentMode: values.contentMode,
        }).unwrap();
        notification.success({ message: "Eveniment creat." });
      }
      navigate(getRouteByName("EventsPage"));
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  if (isEdit && isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  const autocompleteOptions = suggestions.map((s) => ({
    value: s.description,
    label: (
      <Space direction="vertical" size={0}>
        <span style={{ fontWeight: 500 }}>{s.mainText}</span>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {s.secondaryText}
        </Typography.Text>
      </Space>
    ),
    suggestion: s,
  }));

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      disabled={!canEdit}
    >
      <Flex
        justify="space-between"
        align="center"
        gap={16}
        style={{ marginBottom: 16 }}
        wrap="wrap"
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          {isEdit ? "Editează eveniment" : "Eveniment nou"}
        </Typography.Title>
        <Space>
          <Button onClick={() => navigate(getRouteByName("EventsPage"))}>
            Înapoi
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isCreating || isUpdating}
            disabled={!canEdit}
          >
            {isEdit ? "Salvează" : "Creează"}
          </Button>
        </Space>
      </Flex>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Detalii">
            <Form.Item
              name="title"
              label="Titlu"
              rules={[
                { required: true, message: "Introdu un titlu" },
                { max: 200, message: "Maxim 200 caractere" },
              ]}
            >
              <Input placeholder='Ex: "Beach Please discuții în timp real"' />
            </Form.Item>

            <Form.Item
              name="description"
              label="Descriere"
              rules={[{ max: 2000, message: "Maxim 2000 caractere" }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Scurtă descriere a evenimentului (opțional)."
              />
            </Form.Item>

            {!isEdit && (
              <Form.Item
                name="contentMode"
                label="Continut permis"
                extra="Ce pot trimite participantii in chat. Se poate schimba si dupa ce evenimentul a pornit, din pagina de editare."
              >
                <Radio.Group>
                  <Space direction="vertical" size={4}>
                    <Radio value="mixed">Text si media (implicit)</Radio>
                    <Radio value="media_only">
                      Doar foto / video — textul e permis doar ca descriere
                    </Radio>
                    <Radio value="text_only">
                      Doar text — pozele si videoclipurile sunt respinse
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            )}

            <Form.Item
              label="Banner"
              extra="JPG, PNG sau WebP. Max 5MB."
            >
              <Upload
                accept="image/jpeg,image/png,image/webp"
                listType="picture"
                maxCount={1}
                fileList={bannerList}
                beforeUpload={(file) => {
                  setBannerFile(file);
                  setBannerList([
                    {
                      uid: "new",
                      name: file.name,
                      status: "done",
                    },
                  ]);
                  return false;
                }}
                onRemove={() => {
                  setBannerFile(null);
                  setBannerList([]);
                }}
              >
                <Button icon={<UploadOutlined />}>Încarcă banner</Button>
              </Upload>
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Locație + geofence">
            <Form.Item
              label="Țară pentru sugestii"
              extra="Sugestiile Google sunt restrânse la țara aleasă. „Toate țările” caută global."
            >
              <Select
                showSearch
                value={searchCountry}
                onChange={setSearchCountry}
                options={countryOptions}
                loading={isLoadingCountries}
                optionFilterProp="label"
                style={{ width: "100%" }}
                placeholder="Alege o țară"
              />
            </Form.Item>
            <Form.Item
              name="locationLabel"
              label="Adresă"
              rules={[{ required: true, message: "Caută o locație" }]}
              extra="Tastează pentru sugestii (Google Places via backend)."
            >
              <AutoComplete
                options={autocompleteOptions}
                onSearch={handleAutocompleteSearch}
                onSelect={handleAutocompleteSelect}
                allowClear
                placeholder='Ex: "Costinești"'
                notFoundContent={isAutocompleting ? "Caut..." : "Niciun rezultat"}
              />
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="centerLat"
                  label="Latitudine"
                  rules={[
                    { required: true, message: "Selectează o locație" },
                    {
                      type: "number",
                      min: -90,
                      max: 90,
                      message: "Între -90 și 90",
                    },
                  ]}
                >
                  <InputNumber
                    precision={7}
                    style={{ width: "100%" }}
                    placeholder="44.054"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="centerLng"
                  label="Longitudine"
                  rules={[
                    { required: true, message: "Selectează o locație" },
                    {
                      type: "number",
                      min: -180,
                      max: 180,
                      message: "Între -180 și 180",
                    },
                  ]}
                >
                  <InputNumber
                    precision={7}
                    style={{ width: "100%" }}
                    placeholder="28.633"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="radiusMeters"
              label="Raza geofence (metri)"
              rules={[
                { required: true, message: "Introdu raza" },
                {
                  type: "number",
                  min: RADIUS_MIN,
                  max: maxRadiusMeters,
                  message: `Între 50m și ${formatMeters(maxRadiusMeters)}`,
                },
              ]}
              extra={`Doar utilizatorii cu telefonul în această rază pot posta în chat. Restul rămân observatori. Plafonul (${formatMeters(maxRadiusMeters)}) se configurează din Setări platformă.`}
            >
              {/* Slider + InputNumber share the form value. The slider is
                  coarse at the upper end of the range, so the number input
                  keeps precise entry possible. Antd injects `value` and
                  `onChange` into both children via Form.Item's controlled
                  wrapping; `maxMeters` is passed through explicitly. */}
              <RadiusInput maxMeters={maxRadiusMeters} />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Program">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="startsAt"
                  label="Începe"
                  rules={[{ required: true, message: "Selectează data" }]}
                >
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="DD.MM.YYYY HH:mm"
                    style={{ width: "100%" }}
                    placeholder="Selectează data și ora"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="endsAt"
                  label="Se termină"
                  rules={[
                    { required: true, message: "Selectează data" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const starts = getFieldValue("startsAt") as Dayjs | undefined;
                        if (!value || !starts || value.isAfter(starts)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Sfârșitul trebuie să fie după început"),
                        );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="DD.MM.YYYY HH:mm"
                    style={{ width: "100%" }}
                    placeholder="Selectează data și ora"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>

        {isEdit && eventId !== undefined && data && (
          <Col xs={24}>
            <EventContentModeCard
              eventId={eventId}
              status={data.status}
              currentMode={data.contentMode}
            />
          </Col>
        )}

        {isEdit && eventId !== undefined && (
          <Col xs={24}>
            <EventModerationCard eventId={eventId} />
          </Col>
        )}
      </Row>
    </Form>
  );
};
